/**
 * Account-drilldown default ranking (Spencer, 2026-07-28) — pure partition
 * helper. Contacts with ≥1 "Terra Actions" pill (the contact-state read-model
 * strip; see StatePills.tsx / hooks.ts's useContactStatePillsWithSettle)
 * float above contacts with none, preserving the SERVER's own row order
 * within each partition (a stable partition, not a resort). ContactFlowTable
 * feeds this rows already sorted `fit_score DESC`
 * (@shared/flow/contact-view's CONTACT_ACCOUNT_DEFAULT_SORT) for the
 * account-scoped mount, so a stable partition on top of that is exactly
 * "has notes first, fit score desc within each" — this function itself
 * never looks at fit_score or any other field, it only reads presence in
 * `stateMap` and preserves incoming order.
 *
 * Pure + generic (no React, no fetching) so it's unit-testable in isolation:
 * `rows` need only carry an `id`; `stateMap` is whatever
 * useContactStatePillsWithSettle already built (contact id → pills array).
 * A missing entry, or an entry with an empty array, both mean "no notes" —
 * matching the resolver contract that "absent = no signal" (see hooks.ts's
 * useContactStatePillsWithSettle doc comment).
 */
export function rankByStateThenServerOrder<Row extends { id: string }>(
  rows: Row[],
  stateMap: Map<string, unknown[]>,
): Row[] {
  const withNotes: Row[] = [];
  const withoutNotes: Row[] = [];
  for (const row of rows) {
    const pills = stateMap.get(row.id);
    (pills && pills.length > 0 ? withNotes : withoutNotes).push(row);
  }
  return [...withNotes, ...withoutNotes];
}
